"use client"

import { useRef } from "react"
import Image from "next/image"
import { integrationFlowSources, popularIntegrations } from "../_data/integrations"
import { AtlasNodeOrb } from "./AtlasThinkingOrb"
import { useAtlasReveal } from "./useAtlasReveal"

const integrationsRevealTargets = [
  { selector: ".integrations-copy", y: 32 },
  { selector: ".integrations-flow-card", y: 28, scale: 0.96 },
  { selector: ".integration-logo-item", y: 18, stagger: 55 },
] as const

type IntegrationLogoData = {
  name: string
  logo: string
}

function IntegrationLogo({ integration }: { integration: IntegrationLogoData }) {
  return (
    <span className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-white/10 sm:size-16">
      <Image
        src={integration.logo}
        alt=""
        width={64}
        height={64}
        className="size-full object-cover"
      />
    </span>
  )
}

export default function IntegrationsSection() {
  const sectionRef = useRef<HTMLElement>(null)

  useAtlasReveal(sectionRef, integrationsRevealTargets)

  return (
    <section
      ref={sectionRef}
      id="integrations"
      aria-labelledby="integrations-heading"
      className="relative overflow-x-hidden border-y border-slate-200/70 bg-white py-20 dark:border-white/10 dark:bg-gray-900 sm:py-28 lg:py-36"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center lg:gap-16">
          <div className="integrations-copy">
            <h2
              id="integrations-heading"
              className="max-w-3xl text-headline text-slate-950 dark:text-white"
            >
              Работает с тем, чем вы уже пользуетесь.
            </h2>
            <p className="mt-5 max-w-2xl text-lead text-slate-600 dark:text-slate-300">
              CRM, документы, таблицы, мессенджеры и облачные диски подключаются к
              ассистенту через API, MCP или готовые сценарии автоматизации.
            </p>
            <p className="mt-7 inline-flex rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-medium text-slate-500 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-400">
              Подключения к рабочим системам · MCP · API
            </p>
          </div>

          <div className="integrations-flow-card overflow-hidden rounded-2xl border border-slate-200/90 bg-[#f7f6f1] shadow-2xl shadow-slate-950/10 dark:border-white/10 dark:bg-[#121418] dark:shadow-black/30">
            <div className="border-b border-slate-200/80 bg-white px-4 py-4 dark:border-white/10 dark:bg-white/[0.04] sm:px-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-950 dark:text-white">
                    Популярные подключения
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    ассистент собирает контекст из рабочих сервисов компании
                  </p>
                </div>
                <span className="hidden rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300 sm:inline-flex">
                  API готов
                </span>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid gap-3 sm:grid-cols-3">
                {integrationFlowSources.map((source) => (
                  <div
                    key={source.slug}
                    className="rounded-xl border border-slate-200/80 bg-white p-4 dark:border-white/10 dark:bg-white/[0.04]"
                  >
                    <IntegrationLogo integration={source} />
                    <p className="mt-4 text-sm font-semibold text-slate-950 dark:text-white">
                      {source.name}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {source.detail}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-center justify-center gap-3 text-slate-400">
                <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
                <AtlasNodeOrb state="connecting" />
                <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
              </div>
            </div>
          </div>
        </div>

        <ul className="integrations-list mt-12 grid grid-flow-dense grid-cols-1 gap-x-10 gap-y-5 lg:grid-cols-2 xl:grid-cols-3">
          {popularIntegrations.map((integration) => (
            <li key={integration.slug} className="integration-logo-item relative min-w-0">
              <div className="glass-shine group relative flex min-h-[86px] items-center gap-4 overflow-hidden rounded-[1.65rem] border border-transparent p-3 pr-5 transition-all duration-300 hover:-translate-y-1 hover:border-brand-500/30 hover:bg-white hover:shadow-lg hover:shadow-brand-500/10 dark:hover:border-jungle-400/30 dark:hover:bg-white/[0.06] dark:hover:shadow-[0_0_22px_rgba(55,164,131,0.14)]">
                <IntegrationLogo integration={integration} />
                <div className="min-w-0">
                  <h3 className="truncate text-subhead text-slate-950 dark:text-white">
                    {integration.name}
                  </h3>
                  <p className="mt-0.5 line-clamp-2 text-sm text-slate-500 transition group-hover:text-slate-700 dark:text-white/45 dark:group-hover:text-white/60">
                    {integration.detail}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
